import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PatrolGuildRegistryStatus, PatrolModQueueItem } from '../models/patrol-discord-cockpit.model';
import {
  PatrolChannelPolicy,
  PatrolDiscordChannelOption,
  PatrolGuildSettings,
  PatrolPolicyPresetId,
} from '../models/patrol-discord-cockpit.model';

export type { PatrolModQueueItem };

export interface PatrolGuildPoliciesResponse {
  guildSettings: PatrolGuildSettings;
  rolePolicies: unknown[];
  channelPolicies: PatrolChannelPolicy[];
  channelAccessPolicies: unknown[];
  discordRoles: unknown[];
  discordChannels: PatrolDiscordChannelOption[];
}

export type UpdatePatrolGuildPoliciesRequest = {
  guildSettings?: Partial<PatrolGuildSettings>;
  channelPolicies?: Array<{
    discordChannelId: string;
    policyId: PatrolPolicyPresetId;
  }>;
};

export type PatrolRobloxBridgeStatus = 'never_seen' | 'online' | 'stale';

export interface PatrolRobloxExperience {
  registryId: string;
  communityId: string | null;
  displayName: string;
  status: PatrolGuildRegistryStatus;
  memberCount: number;
  linkedMemberCount: number;
  linkedAt?: string | null;
  createdAt?: string;
  inviteUrl?: string | null;
  bridgeLastSeenAt?: string | null;
  bridgeStatus?: PatrolRobloxBridgeStatus | null;
}

export interface PatrolRobloxIngestHealth {
  platform: 'roblox';
  ingestEnabled: boolean;
  allowedUniversesConfigured: boolean;
}

export interface PatrolRobloxStudioScriptEndpoints {
  valvurUrl: string;
  handshakeUrl: string;
  linkStartUrl: string;
  linkPageBaseUrl: string;
}

export interface PatrolRobloxStudioScriptResponse {
  script: string;
  communityId: string;
  endpoints: PatrolRobloxStudioScriptEndpoints;
}

export interface PatrolRobloxStudioScriptRequest {
  channelName?: string;
  channelNames?: string[];
  blockOnDecision?: boolean;
  moderationTimeoutSeconds?: number;
  failClosedOnOutage?: boolean;
  warnMuteSeconds?: number;
}

export interface PatrolRobloxCredentialsStatus {
  registryId: string;
  communityId: string | null;
  hasIngestSecret: boolean;
  hasOpenCloudApiKey: boolean;
  ingestSecretRotatedAt: string | null;
  openCloudApiKeySetAt: string | null;
}

export interface PatrolRobloxIngestSecretGenerated {
  ingestSecret: string;
  rotatedAt: string;
  shownOnce: true;
}

@Injectable({ providedIn: 'root' })
export class PatrolRobloxApiService {
  #http = inject(HttpClient);
  #base = `${environment.baseUrl}/bots/roblox/patrol`;
  readonly ingestUrl = `${environment.baseUrl}/valvur-patrol/roblox/events`;
  readonly handshakeUrl = `${environment.baseUrl}/valvur-patrol/roblox/bridge-handshake`;
  readonly linkStartUrl = `${environment.baseUrl}/valvur-patrol/roblox/link/start`;

  getIngestHealth(): Promise<PatrolRobloxIngestHealth> {
    return lastValueFrom(
      this.#http
        .get<PatrolRobloxIngestHealth>(`${environment.baseUrl}/valvur-patrol/roblox/health`)
        .pipe(take(1)),
    );
  }

  listExperiences(): Promise<PatrolRobloxExperience[]> {
    return lastValueFrom(
      this.#http
        .get<PatrolRobloxExperience[]>(`${this.#base}/me/experiences`)
        .pipe(take(1)),
    );
  }

  getExperiencePolicies(
    communityId: string,
  ): Promise<PatrolGuildPoliciesResponse> {
    return lastValueFrom(
      this.#http
        .get<PatrolGuildPoliciesResponse>(
          `${this.#base}/me/experiences/${encodeURIComponent(communityId)}/policies`,
        )
        .pipe(take(1)),
    );
  }

  updateExperiencePolicies(
    communityId: string,
    body: UpdatePatrolGuildPoliciesRequest,
  ): Promise<PatrolGuildPoliciesResponse> {
    return lastValueFrom(
      this.#http
        .patch<PatrolGuildPoliciesResponse>(
          `${this.#base}/me/experiences/${encodeURIComponent(communityId)}/policies`,
          body,
        )
        .pipe(take(1)),
    );
  }

  createExperience(
    displayName: string,
    universeId: string,
  ): Promise<PatrolRobloxExperience> {
    return lastValueFrom(
      this.#http
        .post<PatrolRobloxExperience>(`${this.#base}/me/experiences`, {
          displayName,
          communityId: universeId.trim(),
        })
        .pipe(take(1)),
    );
  }

  generateStudioScript(
    registryId: string,
    request: PatrolRobloxStudioScriptRequest,
  ): Promise<PatrolRobloxStudioScriptResponse> {
    return lastValueFrom(
      this.#http
        .post<PatrolRobloxStudioScriptResponse>(
          `${this.#base}/me/experiences/${encodeURIComponent(registryId)}/studio-script`,
          request,
        )
        .pipe(take(1)),
    );
  }

  getCredentials(registryId: string): Promise<PatrolRobloxCredentialsStatus> {
    return lastValueFrom(
      this.#http
        .get<PatrolRobloxCredentialsStatus>(
          `${this.#base}/me/experiences/${encodeURIComponent(registryId)}/credentials`,
        )
        .pipe(take(1)),
    );
  }

  generateIngestSecret(
    registryId: string,
  ): Promise<PatrolRobloxIngestSecretGenerated> {
    return lastValueFrom(
      this.#http
        .post<PatrolRobloxIngestSecretGenerated>(
          `${this.#base}/me/experiences/${encodeURIComponent(registryId)}/credentials/ingest-secret`,
          {},
        )
        .pipe(take(1)),
    );
  }

  saveOpenCloudApiKey(
    registryId: string,
    apiKey: string,
  ): Promise<{ configured: true; setAt: string }> {
    return lastValueFrom(
      this.#http
        .patch<{ configured: true; setAt: string }>(
          `${this.#base}/me/experiences/${encodeURIComponent(registryId)}/credentials/open-cloud-key`,
          { apiKey },
        )
        .pipe(take(1)),
    );
  }

  listModQueue(communityId?: string): Promise<PatrolModQueueItem[]> {
    const query = communityId
      ? `?communityId=${encodeURIComponent(communityId)}`
      : '';
    return lastValueFrom(
      this.#http
        .get<PatrolModQueueItem[]>(`${this.#base}/me/mod-queue${query}`)
        .pipe(take(1)),
    );
  }

  approveModQueueItem(id: string): Promise<PatrolModQueueItem> {
    return lastValueFrom(
      this.#http
        .post<PatrolModQueueItem>(
          `${this.#base}/me/mod-queue/${encodeURIComponent(id)}/approve`,
          {},
        )
        .pipe(take(1)),
    );
  }

  rejectModQueueItem(id: string): Promise<PatrolModQueueItem> {
    return lastValueFrom(
      this.#http
        .post<PatrolModQueueItem>(
          `${this.#base}/me/mod-queue/${encodeURIComponent(id)}/reject`,
          {},
        )
        .pipe(take(1)),
    );
  }
}
